import { createStyles } from '@mantine/core';
import { Fragment, type ComponentProps, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';

import Image from '~/domains/common/components/Image/Image';
import { textStyles } from '~/domains/design-system/Text';
import { extractPlayerSurnames } from '~/domains/common/utils/extractPlayerSurnames';
import PicksheetLink from '~/domains/contest/domains/common/components/PicksheetLink/PicksheetLink';
import { Routes } from '~/domains/common/constants/routes';

import playerSillouetteImage from '../../../player/assets/player-sillouette.svg';
import type { ScheduledGamecenterEntryProps } from '../../../common/components/GamecenterEntriesPageScheduled/GamecenterEntriesPageScheduled';
import GamecenterEntry from '../../../common/components/GamecenterEntriesPageScheduled/GamecenterEntry/GamecenterEntry';

const useStyles = createStyles((theme) => ({
  playerLink: {
    '&.isInteractive': {
      transition: 'opacity 150ms ease-in-out',

      '&:hover': {
        opacity: 0.5,
      },
    },
  },
  playerImages: {
    display: 'flex',
    gap: theme.other.spacing._8,
    flexWrap: 'wrap',
    marginTop: theme.other.spacing._8,
  },
  playerImageContainer: {
    background: `linear-gradient(180deg, ${theme.white} 0%, ${theme.colors.gray2[0]} 100%)`,
    borderRadius: theme.other.spacing._4,
    border: `1px solid ${theme.colors.gray2[0]}`,
    width: theme.other.spacing._48 + 4,
    height: theme.other.spacing._48,
    display: 'flex',
    alignItems: 'end',
    justifyContent: 'center',

    '&.isInjured': {
      borderColor: theme.colors.red[6],
    },
  },
  playerImage: {
    objectFit: 'contain',
    objectPosition: 'bottom center',
  },
  playerNames: {
    ...textStyles['body-small'],
    color: theme.colors.gray2[6],
    marginTop: theme.other.spacing._8,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  playerName: {
    color: theme.colors.gray2[6],

    '&.isInjured': {
      color: theme.colors.red[6],
      ...textStyles['subhead-x-small'],
    },
  },
}));

type GamecenterPlayerSlate = Omit<ScheduledGamecenterEntryProps['slates'][number], 'picks'> & {
  picks: (
    | (NonNullable<ScheduledGamecenterEntryProps['slates'][number]['picks'][number]> & {
        isInjured?: boolean;
      })
    | null
  )[];
};

export type GamecenterPlayerEntryProps = Omit<ScheduledGamecenterEntryProps, 'slates'> & {
  slates: GamecenterPlayerSlate[];
};

function EntryScheduledSingleSlate({
  contestId,
  entry,
  openEntryNameEditModal,
}: {
  contestId: string;
  entry: GamecenterPlayerEntryProps;
  openEntryNameEditModal: (entry: { id: string; name: string }) => void;
}) {
  const { classes, theme, cx } = useStyles();
  const { t } = useTranslation('contest');
  const slate = entry.slates[0];
  const picks = slate?.picks ?? [];
  const visiblePicks = picks.filter((pick) => pick !== null);
  const canMakePicks = entry.pickState.kind === 'controller';
  const isMissingPicks = entry.pickState.kind === 'controller' && entry.pickState.isMissingPicks;

  const actionsBanner: ComponentProps<typeof GamecenterEntry>['actionsBanner'] = useMemo(() => {
    if (!slate?.isMakePicks) {
      return {
        content: t('gamecenter.player.common.entryScheduled.playerPoolNotReady'),
        color: 'gray',
      };
    }
    if (isMissingPicks) {
      return {
        content: t('gamecenter.player.common.entryScheduled.missingPicks'),
      };
    }
  }, [entry, isMissingPicks, t]);
  if (!slate) return null;

  const requiredPicksCount = slate.requiredPicksCount;

  return (
    <GamecenterEntry
      key={entry.id}
      contestId={contestId}
      entryId={entry.id}
      name={entry.name}
      isMultiOwner={entry.isMultiOwner}
      pickState={entry.pickState}
      entryOrder={entry.entryOrder}
      requiredPicksCount={requiredPicksCount}
      openEntryNameEditModal={openEntryNameEditModal}
      actionsBanner={actionsBanner}
    >
      {picks?.length ? (
        <PicksheetLink
          href={Routes.gamecenterPicks(contestId, { entryId: entry.id })}
          className={cx(classes.playerLink, { isInteractive: canMakePicks })}
          isEnabled={canMakePicks}
        >
          <div className={classes.playerImages}>
            {visiblePicks.map((pick) => (
              <div
                key={pick.id}
                className={cx(classes.playerImageContainer, { isInjured: pick.isInjured })}
              >
                <Image
                  className={classes.playerImage}
                  src={pick.image}
                  alt={pick.name}
                  width={theme.other.spacing._48}
                  height={theme.other.spacing._48}
                  fallbackImageUrl={playerSillouetteImage.src}
                />
              </div>
            ))}
          </div>
          <div className={classes.playerNames}>
            {visiblePicks.map((pick, i) => (
              <Fragment key={pick.id}>
                <span className={cx(classes.playerName, { isInjured: pick.isInjured })}>
                  {extractPlayerSurnames(pick.name)}
                </span>
                {i < visiblePicks.length - 1 && ', '}
              </Fragment>
            ))}
          </div>
        </PicksheetLink>
      ) : null}
    </GamecenterEntry>
  );
}

export default EntryScheduledSingleSlate;
